import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Tag,
  Typography,
  theme,
  Drawer,
  Button,
  ConfigProvider,
  theme as antTheme,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  BookOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/stores/authStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useHasRealTeachers } from '@/hooks/useHasRealTeachers';
import { ROLE_STYLES, AVATAR_COLORS } from '@/constants';
import NotificationBell from './NotificationBell';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const SIDEBAR_BG = '#1a1d2e';
const SIDEBAR_BORDER = 'rgba(255,255,255,0.08)';

const ALL_MENU_ITEMS = [
  { key: '/student', icon: <HomeOutlined />, label: 'Home' },
  { key: '/student/decks', icon: <BookOutlined />, label: 'My Decks' },
  { key: '/student/teachers', icon: <TeamOutlined />, label: 'My Teachers' },
  { key: '/student/progress', icon: <TrophyOutlined />, label: 'Progress' },
  { key: '/student/profile', icon: <UserOutlined />, label: 'Profile' },
];

function resolveSelectedKey(pathname: string): string {
  if (pathname === '/student' || pathname === '/student/') return '/student';
  const match = ALL_MENU_ITEMS.find(
    (item) => item.key !== '/student' && pathname.startsWith(item.key),
  );
  return match ? match.key : '/student';
}

function getAvatarColor(user: { name: string; id?: string } | null, avatarId?: string | null): string {
  if (avatarId) {
    const idx = parseInt(avatarId, 10);
    if (!isNaN(idx) && idx >= 0 && idx < AVATAR_COLORS.length) {
      return AVATAR_COLORS[idx];
    }
  }
  if (!user?.name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < user.name.length; i++) {
    hash = user.name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function StudentLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const { token: themeToken } = theme.useToken();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const queryClient = useQueryClient();

  const hasRealTeachers = useHasRealTeachers();

  // Hide "My Teachers" menu when no real teachers are connected
  const MENU_ITEMS = useMemo(
    () => hasRealTeachers
      ? ALL_MENU_ITEMS
      : ALL_MENU_ITEMS.filter((item) => item.key !== '/student/teachers'),
    [hasRealTeachers],
  );

  const selectedKey = resolveSelectedKey(location.pathname);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
    if (isMobile) setSidebarOpen(false);
  };

  const handleLogout = () => {
    clearAuth();
    queryClient.clear();
    navigate('/login');
  };

  const avatarDropdownItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/student/profile'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: handleLogout,
    },
  ];

  const roleStyle = ROLE_STYLES.STUDENT;
  const avatarColor = getAvatarColor(user);

  const SidebarContent = () => (
    <>
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '0 18px',
          gap: 10,
          borderBottom: `1px solid ${SIDEBAR_BORDER}`,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: 'linear-gradient(135deg, #f59e0b, #f97316)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 17,
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          M
        </div>
        <Text
          strong
          style={{
            color: '#fff',
            fontSize: 19,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            fontFamily: "'Nunito', 'Segoe UI', sans-serif",
          }}
        >
          CardWise
        </Text>
      </div>

      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        items={MENU_ITEMS}
        onClick={handleMenuClick}
        style={{
          marginTop: 8,
          borderRight: 0,
          background: 'transparent',
          fontSize: 15,
        }}
        theme="dark"
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Desktop Sidebar ── */}
      {!isMobile && (
        <ConfigProvider
          theme={{
            algorithm: antTheme.darkAlgorithm,
            token: { colorPrimary: '#f59e0b', colorBgBase: SIDEBAR_BG },
            components: { Layout: { siderBg: SIDEBAR_BG } },
          }}
        >
          <Sider
            width={220}
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            trigger={null}
            style={{
              overflow: 'auto',
              height: '100vh',
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 10,
              background: SIDEBAR_BG,
            }}
          >
            {collapsed ? (
              <>
                <div
                  style={{
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: `1px solid ${SIDEBAR_BORDER}`,
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 17,
                      fontWeight: 700,
                      color: '#fff',
                    }}
                  >
                    M
                  </div>
                </div>
                <Menu
                  mode="inline"
                  selectedKeys={[selectedKey]}
                  items={MENU_ITEMS}
                  onClick={handleMenuClick}
                  style={{
                    marginTop: 8,
                    borderRight: 0,
                    background: 'transparent',
                    fontSize: 15,
                  }}
                  theme="dark"
                />
              </>
            ) : (
              <SidebarContent />
            )}
          </Sider>
        </ConfigProvider>
      )}

      {/* ── Mobile Sidebar Drawer ── */}
      {isMobile && (
        <ConfigProvider
          theme={{
            algorithm: antTheme.darkAlgorithm,
            token: { colorPrimary: '#f59e0b', colorBgBase: SIDEBAR_BG },
            components: { Layout: { siderBg: SIDEBAR_BG } },
          }}
        >
          <Drawer
            title={null}
            placement="left"
            onClose={() => setSidebarOpen(false)}
            open={sidebarOpen}
            closeIcon={<CloseOutlined style={{ color: '#fff' }} />}
            styles={{ body: { padding: 0, background: SIDEBAR_BG }, header: { background: SIDEBAR_BG, borderBottom: `1px solid ${SIDEBAR_BORDER}` } }}
            width={220}
          >
            <SidebarContent />
          </Drawer>
        </ConfigProvider>
      )}

      {/* ── Main area ── */}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 80 : 220,
          transition: 'margin-left 0.2s',
          background: '#faf8f5',
          minHeight: '100vh',
        }}
      >
        <Header
          style={{
            background: themeToken.colorBgContainer,
            padding: isMobile ? '0 12px' : '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${themeToken.colorBorder}`,
            position: 'sticky',
            top: 0,
            zIndex: 9,
            height: isMobile ? 48 : 64,
            gap: 12,
          }}
        >
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setSidebarOpen(true)}
              style={{
                minWidth: 44,
                minHeight: 44,
                width: 44,
                height: 44,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                color: themeToken.colorTextSecondary,
              }}
            />
          ) : (
            <div
              style={{ cursor: 'pointer', fontSize: 18, color: themeToken.colorTextSecondary }}
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flex: 1, justifyContent: 'flex-end' }}>
            {!isMobile && (
              <Tag
                style={{
                  color: roleStyle.color,
                  background: roleStyle.bg,
                  border: 'none',
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Student
              </Tag>
            )}
            {!isMobile && (
              <Text style={{ fontWeight: 500, fontSize: 15, color: themeToken.colorTextBase }}>
                {user?.name ?? 'Student'}
              </Text>
            )}
            <NotificationBell size={isMobile ? 32 : 36} />
            <Dropdown menu={{ items: avatarDropdownItems }} trigger={['click']} placement="bottomRight">
              <Avatar
                style={{
                  background: avatarColor,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 16,
                }}
                size={isMobile ? 32 : 38}
              >
                {user?.name?.charAt(0)?.toUpperCase() ?? 'S'}
              </Avatar>
            </Dropdown>
          </div>
        </Header>

        <Content
          style={{
            padding: isMobile ? 12 : 24,
            flex: '1 1 auto',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
